import {useAuth} from "../../providers/AuthProvider";
import {useProject} from "../../providers/ClientProjectProvider";
import React, {useMemo} from "react";
import {useIsMutating} from "@tanstack/react-query";
import SOWApprovalForm from "./project-tools/SOWApprovalForm";
import ProjectNotificationsForm from "./project-tools/ProjectNotificationsForm";
import Project from "../../models/Project";


const ClientLeadManagementTools = (props) => {

    const {auth} = useAuth();

    const {
        project,
        isLoading,
        isProjectUpdating,
        setClientLeadUpdatesForProject
    } = useProject();

    const clientSetupForTemplate = project.client_accepts_standard_time_materials_contract;
    const templateInvalid = project.issue_with_standard_contract_template;

    const isMutating = useIsMutating([])

    const isTalentClassificationCompleted = useMemo(() => {
        return project.hasSetOfferingHireTypeForPositions() && project.hasSetTaxClassificationForPositions();
    }, [isProjectUpdating, TB.map(project?.positions, pos => pos.offeringHireType() || pos?.hasCompletedTaxClassificationForCandidate()), isMutating]);

    // const isAppSheetLinkCompleted = useMemo(() => {
    //     if (!auth?.clientOrg()?.appsheet_id || !project?.appsheet_id) {
    //         return false;
    //     }
    //     return !TB.find(project.positions, pos => !pos.appsheet_id);
    // }, [auth?.client_organization?.appsheet_id,isProjectUpdating, project?.appsheet_id, isMutating]);

    const isSOWApprovalCompleted = useMemo(() => {
        if (!project?.client_contract_management_setting) {
            return false;
        }
        return project?.client_contract_management_setting === Project.ContractSettings.NoSow || (
            project.is_client_contract_available && project.hasAutoGeneratedContract()
        ) || project.hasUploadedManualContract();
    }, [isProjectUpdating, project?.documents, isMutating]);


    const isSOWSigned = useMemo(() => {
        return project.client_contract_signed_at
    }, [isProjectUpdating, isMutating]);



    const totalRolesFilled = useMemo(() => {
        return TB.filter(project.positions, pos => pos.hasBeenFilled()).length
    }, [isProjectUpdating, isMutating]);

    const totalRolesClassified = useMemo(() => {
        return TB.filter(project.positions, pos => pos.hasCompletedTaxClassificationForCandidate()).length
    }, [isProjectUpdating, isMutating]);

    const totalRolesEmployedByWripple = useMemo(() => {
        return TB.filter(project.positions, pos => pos.assigned_candidate?.employed_by === 'Wripple').length
    }, [isProjectUpdating, isMutating]);

    const totalRolesEmployedByGL = useMemo(() => {
        return TB.filter(project.positions, pos => pos.assigned_candidate?.employed_by === 'GreenLight').length
    }, [isProjectUpdating, isMutating]);

    const totalRolesWithoutEmployer = useMemo(() => {
        return TB.filter(project.positions, pos => !pos.assigned_candidate?.employed_by).length
    }, [isProjectUpdating, isMutating]);

    const totalRolesSignedContract = useMemo(() => {
        return TB.filter(project.positions, pos => {
            if (pos.assignedCandidate()?.employed_by === 'GreenLight') {
                return pos.hasCompletedTaxClassificationForCandidate();
            }
            return !!pos.latestSignedTalentContract()
        }).length
    }, [isProjectUpdating, isMutating]);


    const totalRolesHaveSetHireType = useMemo(() => {
        return TB.filter(project.positions, pos => {
            return pos.offeringHireType()
        }).length
    }, [isProjectUpdating, isMutating]);

    const isProjectReadyCompleted = useMemo(() => {
        return project.isActive();
    }, [isProjectUpdating, isMutating, project.status]);


    if (isLoading || !project || !auth || !auth.canManageClients()) {
        return null;
    }

    return (
        <div className="container mx-auto max-w-[1225px] bg-white p-4 mb-5 flex flex-col gap-3 client-lead-tools">
            <div className="flex flex-row justify-between gap-[8px] items-center mb-5">
                <p className="text-primary underline text-bold">
                    CLIENT LEAD TOOLS
                </p>
                <div className="flex flex-row justify-end gap-[8px] items-center">
                    {clientSetupForTemplate ? (
                        templateInvalid ? (
                            <React.Fragment>
                                <i className="fas fa-2x text-warning fa-exclamation-circle"/>
                                <p>
                                    {templateInvalid}
                                </p>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <i className="fas fa-2x text-success fa-check-circle"/>
                                <p>
                                    Client is setup for generated SoW
                                </p>
                            </React.Fragment>
                        )
                    ) : (
                        <React.Fragment>
                            <i className="fas fa-2x text-gray fa-times-circle"/>
                            <p>
                                Client not setup for generated SoW
                            </p>
                        </React.Fragment>
                    )}
                </div>
            </div>

            <div className="flex gap-8 justify-between">
                <ProjectNotificationsForm/>

                {/*<ProjectAppSheetForm/>*/}

            </div>


            {/* <ProjectAppSheetForm/>

            <hr className="my-3"/>

            <ProjectNotificationsForm/> */}

            <hr className="my-3"/>

            {/*{project.isFixedFee() ? (*/}
            {/*    <FixedFeeManagementForm/>*/}
            {/*) : (*/}
            {/*    */}
            {/*)}*/}
            {!isProjectReadyCompleted && (
                <div className="flex flex-row items-stretch justify-between px-5 py-6 gap-8">

                    <SOWApprovalForm/>

                    <div
                        className="relative bg-slate-100 px-4 py-3.5 flex flex-col items-stretch justify-start gap-4 min-h-40"
                        style={{flex: 2}}>
                        <div className="absolute left-0 right-0 flex flex-col items-center top-[-12px]">
                            <i className={TB.mapClass("fas text-[24px]", {
                                "text-success fa-check-circle": isTalentClassificationCompleted,
                                "text-danger fa-times-circle": !isTalentClassificationCompleted
                            })}/>
                        </div>
                        <p className="text-lg font-medium text-center whitespace-pre">
                            Talent Tax{'\n'}Classification & Contracts
                        </p>
                        <div className="flex flex-col gap-2.5 flex-grow justify-between max-h-[300px]">
                            <div className="flex flex-col gap-2">
                                <p className="text-sm font-medium flex flex-row items-center gap-1.5">
                                    <i className={TB.mapClass("fas", {
                                        "text-danger fa-times-circle": project.positions.length - totalRolesFilled > 0,
                                        "text-success fa-check-circle": totalRolesFilled === project.positions.length,
                                    })}/>
                                    <span>
                                        {TB.pluralize("Talent position", project.positions.length, true)}
                                    </span>
                                </p>
                                <div className="flex flex-col gap-1 pl-7 mb-0">
                                    {totalRolesFilled > 0 && (
                                        <p className="text-xs">
                                            {TB.pluralize("Position", totalRolesFilled, true)} Filled
                                        </p>
                                    )}
                                    {project.positions.length - totalRolesFilled > 0 && (
                                        <p className="text-xs">
                                            {TB.pluralize("Position", project.positions.length - totalRolesFilled, true)} Still
                                            Open
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <p className="text-sm font-medium flex flex-row items-center gap-1.5">
                                    <i className={TB.mapClass("fas", {
                                        "text-danger fa-times-circle": project.positions.length - totalRolesClassified > 0,
                                        "text-success fa-check-circle": totalRolesClassified === project.positions.length,
                                    })}/>
                                    <span>
                                        Completed tax classifications
                                    </span>
                                </p>
                                {(totalRolesEmployedByWripple + totalRolesEmployedByGL + totalRolesWithoutEmployer) > 0 && (
                                    <div className="flex flex-col gap-1 pl-7 mb-0">
                                        {totalRolesEmployedByWripple > 0 && (
                                            <p className="text-xs">
                                                {totalRolesEmployedByWripple} Employed By Wripple
                                            </p>
                                        )}

                                        {totalRolesEmployedByGL > 0 && (
                                            <p className="text-xs">
                                                {totalRolesEmployedByGL} Employed By Greenlight
                                            </p>
                                        )}

                                        {totalRolesWithoutEmployer > 0 && (
                                            <p className="text-xs">
                                                {totalRolesWithoutEmployer} {totalRolesWithoutEmployer > 1 ? 'Need' : 'Needs'} Classification
                                            </p>
                                        )}
                                    </div>
                                )}

                            </div>
                            <div className="flex flex-col gap-2">
                                <p className="text-sm font-medium flex flex-row items-center gap-1.5">
                                    <i className={TB.mapClass("fas", {
                                        "text-danger fa-times-circle": project.positions.length - totalRolesSignedContract > 0,
                                        "text-success fa-check-circle": totalRolesSignedContract === project.positions.length,
                                    })}/>
                                    <span>
                                        Talent signed contracts
                                    </span>
                                </p>
                                <div className="flex flex-col gap-1 pl-7 mb-0">
                                    {totalRolesSignedContract > 0 && (
                                        <p className="text-xs">
                                            {TB.pluralize("Contract", totalRolesSignedContract, true)} Signed
                                        </p>
                                    )}
                                    {project.positions.length - totalRolesSignedContract > 0 && (
                                        <p className="text-xs">
                                            {TB.pluralize("Contract", project.positions.length - totalRolesSignedContract, true)} Missing
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <p className="text-sm font-medium flex flex-row items-center gap-1.5">
                                    <i className={TB.mapClass("fas", {
                                        "text-danger fa-times-circle": project.positions.length - totalRolesHaveSetHireType > 0,
                                        "text-success fa-check-circle": totalRolesHaveSetHireType === project.positions.length,
                                    })}/>
                                    <span>
                                        Offering Hire Type Determined
                                    </span>
                                </p>
                                <div className="flex flex-col gap-1 pl-7 mb-0">
                                    {totalRolesHaveSetHireType > 0 && (
                                        <p className="text-xs">
                                            {TB.pluralize("Hire Type", totalRolesHaveSetHireType, true)} Selected
                                        </p>
                                    )}
                                    {project.positions.length - totalRolesHaveSetHireType > 0 && (
                                        <p className="text-xs">
                                            {TB.pluralize("Hire Type", project.positions.length - totalRolesHaveSetHireType, true)} Missing
                                        </p>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>

                    <div
                        className="min-w-[315px] flex-1 relative bg-slate-100 px-4 py-3.5 flex flex-col items-stretch justify-between gap-4 min-h-40">
                        <div className="absolute left-0 right-0 flex flex-col items-center top-[-12px]">
                            <i className={TB.mapClass("fas text-[24px]", {
                                "text-success fa-check-circle": isProjectReadyCompleted,
                                "text-danger fa-times-circle": !isProjectReadyCompleted
                            })}/>
                        </div>
                        <p className="text-lg font-medium text-center">
                            Project Active?
                        </p>

                        {(isSOWSigned && isTalentClassificationCompleted && isSOWApprovalCompleted) ? (
                            <div className="flex-1 flex flex-col items-center justify-center gap-3">
                                <p className="font-semibold">
                                    Required Steps Completed
                                </p>
                                <button type="button"
                                        onClick={async () => {

                                            await setClientLeadUpdatesForProject({is_active: true});
                                        }}
                                        className="btn btn-primary text-sm min-w-[165px]"
                                        disabled={isProjectUpdating}>

                                    Make Project Active
                                </button>
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center pb-5">
                                <p className="text-center flex flex-col items-center gap-3 justify-center">

                                    {!isSOWApprovalCompleted ? (
                                        <span>
                                            SOW Approval setting must been determined before you can make the project active.
                                        </span>
                                    ) : (!isSOWSigned && (
                                        <span>
                                            SOW must be approved by Client before you can make the project active.
                                        </span>
                                    ))}
                                    {!isTalentClassificationCompleted && (
                                        <span>
                                            All talent tax classifications must be set and contracts must be uploaded and signed.
                                        </span>
                                    )}
                                    {/*    {!isAppSheetLinkCompleted && (*/}
                                    {/*        <span>*/}
                                    {/*    AppSheet IDs for Client, Project and each candidate must be linked.*/}
                                    {/*</span>*/}
                                    {/*    )}*/}
                                </p>

                            </div>
                        )}
                    </div>

                </div>
            )}
            <div className="flex flex-row justify-start gap-[24px] items-center">

                <p className="text-bold whitespace-nowrap">
                    Contract Status:
                </p>
                <p className="whitespace-nowrap">
                    {!project.hasRequestedSOW() && project.isBYO() && !project.hasFilledAllPositions() && (
                        <span>Candidates have not been selected for all positions.</span>
                    )}
                    {!project.hasRequestedSOW() && project.isService() && !project.assigned_team_candidate && (
                        <span>Team Candidate proposal has not been selected.</span>
                    )}

                    {!project.hasRequestedSOW() && project.canRequestSOW() && (
                        <span>Client has not requested SOW yet.</span>
                    )}
                    {project.hasRequestedSOW() && project.isAwaitingContract() && (
                        <span>Client has requested and is awaiting SOW.</span>
                    )}
                    {project.hasRequestedSOW() && project.isPendingSignedContract() && (
                        <span>Contract available for Client to approve.</span>
                    )}
                    {project.hasRequestedSOW() && project.isAwaitingKickoff() && (
                        <span>Contract has been approved/signed for Client and project is awaiting kickoff.</span>
                    )}
                </p>

            </div>


        </div>
    )
}


export default ClientLeadManagementTools;